import React from 'react';
import { useTranslation } from 'react-i18next';
import LegalPage from './LegalPage';

function EULA() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  const content = isEnglish ? (
    <>
      <h2>1. Agreement to Terms</h2>
      <p>By downloading, installing, or using the Alhamdulillah mobile application ("the App"), you agree to be bound by this End User License Agreement ("EULA"). If you do not agree to these terms, do not download, install, or use the App.</p>

      <h2>2. License Grant</h2>
      <p>We grant you a limited, non-exclusive, non-transferable, revocable license to use the Alhamdulillah App for your personal, non-commercial use on devices that you own or control, subject to the terms of this EULA.</p>

      <h3>2.1 Permitted Uses</h3>
      <ul>
        <li>Download and install the App on your personal devices</li>
        <li>Use the App for personal spiritual practice and gratitude tracking</li>
        <li>Create and store personal content within the App</li>
        <li>Access features including voice recording, journaling, and dhikr content</li>
      </ul>

      <h3>2.2 Prohibited Uses</h3>
      <ul>
        <li>Modify, reverse engineer, decompile, or disassemble the App</li>
        <li>Remove or alter any copyright, trademark, or proprietary notices</li>
        <li>Use the App for any commercial purpose without written permission</li>
        <li>Share your account credentials with others</li>
        <li>Upload malicious code or attempt to compromise the App's security</li>
      </ul>

      <h2>3. Pricing and Optional Subscriptions</h2>
      <h3>3.1 Free Version</h3>
      <p>The App is available for free with full core functionality. <strong>All subscriptions are completely optional.</strong></p>

      <h3>3.2 Optional Premium Subscriptions</h3>
      <p>We offer optional premium subscriptions:</p>
      <ul>
        <li><strong>Monthly Premium:</strong> €5.99/month with auto-renewal</li>
        <li><strong>Lifetime Premium:</strong> €24.99 one-time payment</li>
      </ul>

      <h3>3.3 Payment Terms</h3>
      <ul>
        <li>Payments are processed through the App Store (iOS) or Google Play Store (Android)</li>
        <li>Monthly subscriptions automatically renew unless cancelled</li>
        <li>You can cancel at any time through your app store account settings</li>
        <li>Refunds are subject to App Store/Google Play policies</li>
      </ul>

      <h2>4. Intellectual Property Rights</h2>
      <p>The Alhamdulillah App, including all content, features, functionality, design elements, dhikr texts, and software, is owned by us and is protected by international copyright, trademark, and other intellectual property laws.</p>

      <h2>5. User Responsibilities</h2>
      <p>You are responsible for maintaining the confidentiality of your account credentials, all activities that occur under your account, ensuring your device meets the App's technical requirements, and complying with all applicable laws when using the App.</p>

      <h2>6. Disclaimer of Warranties</h2>
      <p>THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.</p>

      <h2>7. Limitation of Liability</h2>
      <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE APP.</p>

      <h2>8. Termination</h2>
      <p>This license is effective until terminated. We may terminate or suspend your access to the App at any time. Upon termination, you must cease all use of the App and delete all copies from your devices.</p>

      <h2>9. Contact Information</h2>
      <p>If you have questions about this EULA, please contact us through the App's support section or website.</p>
    </>
  ) : (
    <>
      <h2>1. Acceptation des Conditions</h2>
      <p>En téléchargeant, installant ou utilisant l'application mobile Alhamdulillah ("l'Application"), vous acceptez d'être lié par ce Contrat de Licence Utilisateur Final ("CLUF"). Si vous n'acceptez pas ces conditions, ne téléchargez pas, n'installez pas et n'utilisez pas l'Application.</p>

      <h2>2. Octroi de Licence</h2>
      <p>Nous vous accordons une licence limitée, non exclusive, non transférable et révocable pour utiliser l'Application Alhamdulillah pour votre usage personnel et non commercial sur les appareils que vous possédez ou contrôlez, sous réserve des conditions de ce CLUF.</p>

      <h3>2.1 Utilisations Autorisées</h3>
      <ul>
        <li>Télécharger et installer l'Application sur vos appareils personnels</li>
        <li>Utiliser l'Application pour la pratique spirituelle personnelle et le suivi de la gratitude</li>
        <li>Créer et stocker du contenu personnel dans l'Application</li>
        <li>Accéder aux fonctionnalités incluant l'enregistrement vocal, le journal et le contenu dhikr</li>
      </ul>

      <h3>2.2 Utilisations Interdites</h3>
      <ul>
        <li>Modifier, procéder à l'ingénierie inverse, décompiler ou désassembler l'Application</li>
        <li>Retirer ou modifier les mentions de droits d'auteur, marques ou avis propriétaires</li>
        <li>Utiliser l'Application à des fins commerciales sans autorisation écrite</li>
        <li>Partager vos identifiants de compte avec d'autres personnes</li>
        <li>Télécharger du code malveillant ou tenter de compromettre la sécurité de l'Application</li>
      </ul>

      <h2>3. Tarification et Abonnements Optionnels</h2>
      <h3>3.1 Version Gratuite</h3>
      <p>L'Application est disponible gratuitement avec toutes les fonctionnalités principales. <strong>Tous les abonnements sont complètement optionnels.</strong></p>

      <h3>3.2 Abonnements Premium Optionnels</h3>
      <p>Nous proposons des abonnements premium optionnels :</p>
      <ul>
        <li><strong>Premium Mensuel :</strong> 5,99€/mois avec renouvellement automatique</li>
        <li><strong>Premium à Vie :</strong> 24,99€ paiement unique</li>
      </ul>

      <h3>3.3 Conditions de Paiement</h3>
      <ul>
        <li>Les paiements sont traités via l'App Store (iOS) ou Google Play Store (Android)</li>
        <li>Les abonnements mensuels se renouvellent automatiquement sauf annulation</li>
        <li>Vous pouvez annuler à tout moment via les paramètres de votre compte app store</li>
        <li>Les remboursements sont soumis aux politiques App Store/Google Play</li>
      </ul>

      <h2>4. Droits de Propriété Intellectuelle</h2>
      <p>L'Application Alhamdulillah, y compris tout contenu, fonctionnalités, éléments de conception, textes de dhikr et logiciels, nous appartient et est protégée par les lois internationales sur les droits d'auteur, les marques et autres droits de propriété intellectuelle.</p>

      <h2>5. Responsabilités de l'Utilisateur</h2>
      <p>Vous êtes responsable de maintenir la confidentialité de vos identifiants de compte, toutes les activités qui se produisent sous votre compte, vous assurer que votre appareil répond aux exigences techniques de l'Application et respecter toutes les lois applicables lors de l'utilisation de l'Application.</p>

      <h2>6. Exclusion de Garanties</h2>
      <p>L'APPLICATION EST FOURNIE "TELLE QUELLE" ET "SELON DISPONIBILITÉ" SANS GARANTIES D'AUCUNE SORTE, EXPRESSES OU IMPLICITES, Y COMPRIS MAIS SANS S'Y LIMITER LES GARANTIES IMPLICITES DE QUALITÉ MARCHANDE, D'ADÉQUATION À UN USAGE PARTICULIER ET DE NON-CONTREFAÇON.</p>

      <h2>7. Limitation de Responsabilité</h2>
      <p>DANS LA MESURE MAXIMALE AUTORISÉE PAR LA LOI, NOUS NE SERONS PAS RESPONSABLES DES DOMMAGES INDIRECTS, ACCESSOIRES, SPÉCIAUX, CONSÉCUTIFS OU PUNITIFS DÉCOULANT DE OU LIÉS À VOTRE UTILISATION DE L'APPLICATION.</p>

      <h2>8. Résiliation</h2>
      <p>Cette licence est effective jusqu'à sa résiliation. Nous pouvons résilier ou suspendre votre accès à l'Application à tout moment. À la résiliation, vous devez cesser toute utilisation de l'Application et supprimer toutes les copies de vos appareils.</p>

      <h2>9. Coordonnées</h2>
      <p>Si vous avez des questions sur ce CLUF, veuillez nous contacter via la section support de l'Application ou le site web.</p>
    </>
  );

  return (
    <LegalPage
      title={isEnglish ? 'End User License Agreement (EULA)' : 'Contrat de Licence Utilisateur Final (CLUF)'}
      content={content}
    />
  );
}

export default EULA;
